

import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Typography,
    TextField,
    Grid,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Select,
    MenuItem,
    FormControl,
    Switch,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Snackbar,
    Alert,
    InputLabel,
    IconButton
} from "@mui/material";
import { Edit as EditIcon } from "@mui/icons-material";
import * as employeeService from "../../../Servicefile/employeeService";
import { useLoader } from "../../../../context/LoaderProvider";

// Employee Roles Enum based on your API
const EMPLOYEE_ROLES = [
    { value: "HEAD", label: "Head" },
    { value: "FINANCE", label: "Finance" },
    { value: "MANAGER", label: "Manager" },
    { value: "SUPPORT", label: "Support" }
];

export default function EmployeeManager() {
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState(null);

    // Add these state variables with your other useState declarations
    const [openEdit, setOpenEdit] = useState(false);
    const [editForm, setEditForm] = useState({
        id: null,
        vendorId: null,
        name: "",
        role: "",
        email: "",
        department: "",
        phone: "",
        location: "",
        joined: "",
        status: "",
        remarks: "",
        exitDate: ""
    });

    // Filter states
    const [search, setSearch] = useState("");
    const [role, setRole] = useState("");
    const [department, setDepartment] = useState("");
    const [location, setLocation] = useState("");
    const [status, setStatus] = useState("");

    // Dialog states
    const [openAdd, setOpenAdd] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [stats, setStats] = useState({
        total: 0,
        active: 0,
        inactive: 0
    });
    // Form state
    const [form, setForm] = useState({
        name: "",
        role: "",
        email: "",
        department: "",
        phone: "",
        location: "",
        joined: ""
    });

    const { showLoader, hideLoader } = useLoader();

    // Fetch employees on mount
    useEffect(() => {
        fetchEmployees();
    }, []);

    const showSnackbarMessage = (message, severity = "success") => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Fetch employees from API
    const fetchEmployees = async () => {
        setLoading(true);
        showLoader();
        setApiError(null);

        try {
            const data = await employeeService.fetchEmployees();

            const formattedEmployees = (data || []).map(emp =>
                employeeService.formatEmployeeForDisplay(emp)
            );

            setEmployees(formattedEmployees);

            // ✅ CALCULATE COUNTS
            const activeCount = formattedEmployees.filter(emp => emp.status === "Active").length;

            const inactiveCount = formattedEmployees.filter(emp => emp.status === "Inactive").length;

            // ✅ SAVE TO STATE (IMPORTANT)
            setStats({
                total: formattedEmployees.length,
                active: activeCount,
                inactive: inactiveCount
            });

         

        } catch (error) {
            console.error("Failed to fetch employees:", error);
            setApiError(error.message);
            showSnackbarMessage(`Failed to fetch employees: ${error.message}`, "error");
        } finally {
            setLoading(false);
            hideLoader();
        }
    };
    // Add employee
    const addEmployee = async () => {
        try {
            showLoader();

            const response = await employeeService.addEmployee({
                ...form,
                createdAt: new Date().toISOString()
            });


            if (!response || !response.id) {
                throw new Error("Employee not created properly");
            }

            showSnackbarMessage("Employee added successfully!");

            setOpenAdd(false);
            fetchEmployees();

        } catch (error) {
            console.error("Failed to add employee:", error);
            showSnackbarMessage(error.message, "error");
        } finally {
            hideLoader();
        }
    };

    // Update status (Active/Inactive)
    const updateStatus = async (id, currentStatus) => {
        const employee = employees.find(e => e.id === id);
        if (!employee || !employee.vendorId) return;

        const newStatus = currentStatus === "Active" ? "Inactive" : "Active";

        try {
            showLoader();
            await employeeService.updateEmployeeStatus(employee.vendorId, newStatus);
            showSnackbarMessage(`Employee status updated to ${newStatus}`);
            fetchEmployees();
        } catch (error) {
            console.error("Failed to update status:", error);
            showSnackbarMessage(error.message, "error");
        } finally {
            hideLoader();
        }
    };

    // Deactivate employee
    const deactivate = async (id) => {
        const employee = employees.find(e => e.id === id);
        if (!employee || !employee.vendorId) return;

        if (!window.confirm(`Are you sure you want to deactivate ${employee.name}?`)) return;

        try {
            showLoader();
            await employeeService.deactivateEmployee(employee.vendorId);
            showSnackbarMessage("Employee deactivated successfully");
            fetchEmployees();
        } catch (error) {
            console.error("Failed to deactivate employee:", error);
            showSnackbarMessage(error.message, "error");
        } finally {
            hideLoader();
        }
    };

    // Export CSV
    const exportCSV = () => {
        const headers = [
            "ID",
            "Name",
            "Role",
            "Department",
            "Phone",
            "Location",
            "Supervisor",
            "Joined Date",
            "Status"
        ];

        const rows = filtered.map(e => [
            e.id,
            e.name,
            e.role,
            e.department,
            e.phone,
            e.location,
            e.supervisor,
            e.joined,
            e.status
        ]);

        const csvContent = [
            headers.join(","),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
        ].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `employees_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);

        showSnackbarMessage("Employee data exported successfully!");
    };

    // Edit employee (placeholder for now)
    // Replace the existing handleEdit function
    const handleEdit = (employee) => {
        setEditForm({
            id: employee.id,
            name: employee.name,

            // ✅ FIXED mapping from API
            role: employee.handlingRole,

            email: employee.email,
            department: employee.department,
            phone: employee.phoneNumber, // ⚠️ API uses phoneNumber
            location: employee.location,
            joined: employee.registerTime,

            // ✅ Convert boolean → UI string
            status: employee.enabled ? "Active" : "Inactive",

            remarks: employee.remarks || "",
            exitDate: employee.exitDate || ""
        });
        setOpenEdit(true);
    };


    const saveEdit = async () => {
        try {
            showLoader();

            await employeeService.updateEmployee(
                editForm.id,
                {
                    role: editForm.role,                     // ✅ FIXED
                    status: editForm.status,                 // UI → API handled in service
                    remarks: editForm.remarks || "",         // safe fallback
                    exitDate: editForm.exitDate || null      // avoid empty string issue
                }
            );

            showSnackbarMessage("Employee updated successfully!");
            setOpenEdit(false);
            fetchEmployees();

        } catch (error) {
            console.error("Failed to update employee:", error);
            showSnackbarMessage(error.message, "error");
        } finally {
            hideLoader();
        }
    };
    // Filter employees
    const filtered = employees.filter(emp => {
        const searchText = search.toLowerCase();

        return (
            (emp.name?.toLowerCase()?.includes(searchText) ||

                String(emp.id || "").toLowerCase().includes(searchText) ||

                emp.phone?.toString()?.includes(searchText)
            ) &&
            (role === "" || emp.role === role) &&
            (department === "" || emp.department === department) &&
            (location === "" || emp.location === location) &&
            (status === "" || emp.status === status)
        );
    });

    return (
        <>
            <Box p={0} className="mobile-screen">
                {/* Snackbar for notifications */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={3000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                >
                    <Alert severity={snackbar.severity} onClose={handleCloseSnackbar}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>

                {/* ACTION BUTTONS */}
                <Box display="flex" gap={2} mb={2} flexWrap="wrap">
                    <Button
                        variant="contained"
                        onClick={() => setOpenAdd(true)}
                        sx={{ backgroundColor: "#e66d33", "&:hover": { backgroundColor: "#e66d33" } }}
                    >
                        Add Employee
                    </Button>

                    <Button
                        variant="outlined"
                        onClick={exportCSV}
                        sx={{ borderColor: "#e66d33", color: "#e66d33" }}
                    >
                        Export Data
                    </Button>
                </Box>

                {/* FILTER BAR */}
                <Grid container spacing={2} mb={3}>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            placeholder="Search by name, ID, or phone..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            size="small"
                        />
                    </Grid>

                    <Grid item xs={6} sm={3} md={2}>
                        <FormControl fullWidth size="small">
                            <Select
                                displayEmpty
                                value={role}
                                onChange={(e) => setRole(e.target.value)}
                            >
                                <MenuItem value="">All Roles</MenuItem>
                                {EMPLOYEE_ROLES.map(roleOption => (
                                    <MenuItem key={roleOption.value} value={roleOption.value}>
                                        {roleOption.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>



                    {/* <Grid item xs={6} sm={3} md={2}>
                        <FormControl fullWidth size="small">
                            <Select
                                displayEmpty
                                value={location}
                                onChange={(e) => setLocation(e.target.value)}
                            >
                                <MenuItem value="">All Locations</MenuItem>
                                <MenuItem>Delhi</MenuItem>
                                <MenuItem>Mumbai</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid> */}

                    <Grid item xs={6} sm={3} md={2}>
                        <FormControl fullWidth size="small">
                            <Select
                                displayEmpty
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                            >
                                <MenuItem value="">All Status</MenuItem>
                                <MenuItem>Active</MenuItem>
                                <MenuItem>Inactive</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                {/* TABLE */}
                <Paper sx={{ maxHeight: "42vh", overflow: "auto", scrollbarWidth: "thin", }}>
                    {loading && employees.length === 0 ? (
                        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Table>
                            <TableHead sx={{
                                background: "#e66d33",
                                position: "sticky",
                                top: 0,
                                zIndex: 10
                            }}>
                                <TableRow sx={{ background: "#e66d33" }}>
                                    <TableCell className="table-header-cell">Name</TableCell>
                                    <TableCell className="table-header-cell">Email</TableCell>
                                    {/* <TableCell className="table-header-cell">User Id</TableCell>
                                    <TableCell className="table-header-cell">Password</TableCell> */}
                                    <TableCell className="table-header-cell">Joined Date</TableCell>
                                    {/* <TableCell className="table-header-cell">Department</TableCell> */}
                                    <TableCell className="table-header-cell">Role</TableCell>
                                    <TableCell className="table-header-cell">Phone</TableCell>
                                    {/* <TableCell className="table-header-cell">Location</TableCell> */}
                                    {/* <TableCell className="table-header-cell">Supervisor</TableCell> */}
                                    <TableCell className="table-header-cell">Status</TableCell>
                                    {/* <TableCell className="table-header-cell">Exit Date</TableCell> */}
                                    <TableCell className="table-header-cell">Remarks</TableCell>     {/* NEW */}

                                    <TableCell className="table-header-cell">Actions</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {filtered.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                                            {apiError ? `Error: ${apiError}` : "No employees found"}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filtered.map((emp, index) => (
                                        <TableRow key={emp.id} hover>
                                            {/* <TableCell style={{ whiteSpace: "nowrap" }} className="table-body-cell">{emp.id}</TableCell> */}
                                            <TableCell className="table-body-cell">{emp.name}</TableCell>
                                            <TableCell className="table-body-cell">{emp.email}</TableCell>
                                            {/* <TableCell className="table-body-cell">{emp.userid || "-"}</TableCell>
                                            <TableCell className="table-body-cell">{emp.password || "-"}</TableCell> */}
                                            <TableCell>
                                                {new Date(emp.joined).toLocaleDateString("en-GB")}
                                            </TableCell>
                                            {/* <TableCell className="table-body-cell">{emp.department}</TableCell> */}
                                            <TableCell className="table-body-cell">{emp.role}</TableCell>
                                            <TableCell className="table-body-cell">{emp.phone}</TableCell>
                                            {/* <TableCell className="table-body-cell">{emp.location}</TableCell> */}
                                            <TableCell className="table-body-cell">
                                                <Switch
                                                    checked={emp.status === "Active"}
                                                    onChange={() => updateStatus(emp.id, emp.status)}
                                                    color="success"
                                                    size="small"
                                                />
                                                <span>{emp.status}</span>
                                            </TableCell>
                                            <TableCell className="table-body-cell">{emp.exitDate || "-"}</TableCell>



                                            <TableCell className="table-body-cell">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEdit(emp)}
                                                    sx={{ color: "#e66d33" }}
                                                    title="Edit Employee"
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>

                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    )}
                </Paper>

                {/* ADD EMPLOYEE DIALOG */}
                <Dialog open={openAdd} onClose={() => setOpenAdd(false)} fullWidth maxWidth="sm">
                    <DialogTitle sx={{ bgcolor: "#e66d33", color: "white" }}>
                        Add New Employee
                    </DialogTitle>

                    <DialogContent sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Full Name"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    required
                                    size="small"
                                    sx={{ marginTop: "10px" }}
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Phone"
                                    value={form.phone}
                                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                    required
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Email"
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    required
                                    size="small"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControl fullWidth size="small" required>
                                    <InputLabel id="employee-role-label">Employee Role</InputLabel>
                                    <Select
                                        labelId="employee-role-label"
                                        label="Employee Role"
                                        value={form.role}
                                        onChange={(e) => setForm({ ...form, role: e.target.value })}
                                    >
                                        <MenuItem value="" disabled>
                                            <em>Select a role</em>
                                        </MenuItem>
                                        {EMPLOYEE_ROLES.map(roleOption => (
                                            <MenuItem key={roleOption.value} value={roleOption.value}>
                                                {roleOption.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Location"
                                    value={form.location}
                                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                                    size="small"
                                />
                            </Grid> */}
                        </Grid>
                    </DialogContent>

                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={() => setOpenAdd(false)} variant="outlined">
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={addEmployee}
                            sx={{ backgroundColor: "#e66d33", "&:hover": { backgroundColor: "#e66d33" } }}
                        >
                            Save
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* EDIT EMPLOYEE DIALOG */}
                {/* EDIT EMPLOYEE DIALOG */}
                <Dialog open={openEdit} onClose={() => setOpenEdit(false)} fullWidth maxWidth="sm">
                    <DialogTitle sx={{ bgcolor: "#e66d33", color: "white" }}>
                        Edit Employee - {editForm.name}
                    </DialogTitle>

                    <DialogContent sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                            {/* Status Toggle */}
                            <Grid item xs={12}>
                                <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2, mt: 2 }}>
                                    <Typography variant="body1" fontWeight="500">
                                        Employee Status:
                                    </Typography>
                                    <Box display="flex" alignItems="center" gap={2}>
                                        <Typography variant="body2" color={editForm.status === "Active" ? "green" : "red"}>
                                            {editForm.status === "Active" ? "Active" : "Inactive"}
                                        </Typography>
                                        <Switch
                                            checked={editForm.status === "Active"}
                                            onChange={(e) => setEditForm({
                                                ...editForm,
                                                status: e.target.checked ? "Active" : "Inactive"
                                            })}
                                            color="success"
                                        />
                                    </Box>
                                </Box>
                            </Grid>

                            {/* 🔥 NEW: Role Selection */}
                            <Grid item xs={12}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="edit-role-label">Employee Role</InputLabel>
                                    <Select
                                        labelId="edit-role-label"
                                        label="Employee Role"
                                        value={editForm.role}
                                        onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                                    >
                                        {EMPLOYEE_ROLES.map(roleOption => (
                                            <MenuItem key={roleOption.value} value={roleOption.value}>
                                                {roleOption.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>


                            {/* Remarks */}

                        </Grid>
                    </DialogContent>

                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={() => setOpenEdit(false)} variant="outlined">
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={saveEdit}
                            sx={{ backgroundColor: "#e66d33", "&:hover": { backgroundColor: "#e66d33" } }}
                        >
                            Save Changes
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
            <style>{`
                .table-header-cell {
                    color: white !important;
                    font-weight: 600 !important;
                    font-size: 14px;
                    text-align: center;
                    padding: 12px 16px;
                    border-right: 1px solid rgba(255, 255, 255, 0.2);
                }

                .table-header-cell:last-child {
                    border-right: none;
                }
                
                .table-body-cell {
                    font-weight: 500;
                    text-align:center;
                    padding:6px;
                }
                    @media(max-width :500px){
                    .mobile-screen{
                    width:100%;
                    
                    }
                    }
            `}</style>
        </>
    );
}